import { appConfig } from 'config/app'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, getErrorStatus, isRateLimited } from '@/composables/useAxiosError'
import { useJwtStore } from '@/stores'
import { AvvAcceptResponse, AvvStatus } from '@/types'

/**
 * The data processing agreement's document and its acceptance. Shared by the
 * AVV page and the consent screen, which offers the acceptance inline when an
 * approval waits for it.
 */

/** A request for the agreement document that was refused; 403 is an unverified e-mail address. */
export class AvvRequestError extends Error {
  constructor(readonly status: number) {
    super(`AVV request answered ${status}`)
    this.name = 'AvvRequestError'
  }
}

/**
 * The agreement as a PDF: the stored copy once accepted, otherwise a preview of
 * the current version filled in with the account's data. Fetched rather than
 * linked, because the request carries the session's bearer token. Typed as a
 * PDF whatever the response says, because the blob is framed on our origin and
 * any other body, such as a proxy's error page, would render there as a page.
 */
export const fetchAvvPdf = async (accepted: boolean): Promise<Blob> => {
  const query = accepted ? '' : '?preview=true'
  const response = await fetch(`${appConfig.env.baseUrl}/account/av${query}`, {
    headers: { Authorization: `Bearer ${useJwtStore().getJwt}` }
  })
  if (!response.ok) throw new AvvRequestError(response.status)
  return new Blob([await response.arrayBuffer()], { type: 'application/pdf' })
}

/**
 * The i18n key for a refused AVV request; null for any other failure. Only the
 * coded refusal means an unverified address: an acting context is refused
 * without a body, and any 403 left over is a session that is not the holder's.
 */
export const avvRefusalKey = (err: unknown): string | null => {
  if (isRateLimited(err)) return 'common.tooManyRequests'
  if (getErrorStatus(err) !== 403) return null
  const code = getBackendErrorCode(err)
  if (code === 'email_not_verified') return 'dashboard.account.avv.emailNotVerified'
  if (code === 'action_blocked_during_impersonation') return 'dashboard.impersonation.error.blocked'
  return 'dashboard.account.avv.holderOnly'
}

/** The account's standing on the agreement; null for an answer of an unexpected shape. */
export const fetchAvvStatus = async (): Promise<AvvStatus | null> => {
  const res = await useAxios<null, AvvStatus>('GET', '/account/av/status', true)
  const parsed = AvvStatus.safeParse(res.data)
  return parsed.success ? parsed.data : null
}

/** Accepts the current version for the signed-in account; null for an answer of an unexpected shape. */
export const acceptAvv = async (): Promise<AvvAcceptResponse | null> => {
  const res = await useAxios<null, AvvAcceptResponse>('POST', '/account/av/accept', true)
  const parsed = AvvAcceptResponse.safeParse(res.data)
  return parsed.success ? parsed.data : null
}
